import { cloneDeep, flatMap, get, isPlainObject, set, take } from "lodash-es";

interface SearchSerialization {
  parseSearch: (searchStr: string) => Record<string, unknown>;
  stringifySearch: (search: Record<string, unknown>) => string;
}

type SearchPathSegment = string | number;
type SearchParamEntry = [string, string];

const UNSAFE_PATH_SEGMENTS = new Set(["__proto__", "prototype", "constructor"]);
const ARRAY_INDEX_PATTERN = /^(0|[1-9]\d*)$/;
const NUMERIC_PATH_SEGMENT_PATTERN = /^\d+$/;
const MAX_SEARCH_PARAMS = 100;
const MAX_SEARCH_KEY_LENGTH = 200;
const MAX_SEARCH_PATH_DEPTH = 8;
const MAX_SEARCH_ARRAY_LENGTH = MAX_SEARCH_PARAMS;

function isSerializableScalar(value: unknown): value is string | number | boolean {
  return ["string", "number", "boolean"].includes(typeof value);
}

function isArrayIndexSegment(segment: string): boolean {
  return (
    ARRAY_INDEX_PATTERN.test(segment) &&
    Number.isSafeInteger(Number(segment)) &&
    Number(segment) < MAX_SEARCH_ARRAY_LENGTH
  );
}

function isSafePathSegment(segment: string): boolean {
  return segment.length > 0 && !UNSAFE_PATH_SEGMENTS.has(segment);
}

function toSearchPath(key: string): SearchPathSegment[] | undefined {
  if (key.length === 0 || key.length > MAX_SEARCH_KEY_LENGTH) return undefined;

  const rawSegments = key.split(".");
  if (rawSegments.length > MAX_SEARCH_PATH_DEPTH) return undefined;
  if (!rawSegments.every(isSafePathSegment)) return undefined;

  const path: SearchPathSegment[] = [];
  for (const segment of rawSegments) {
    if (!NUMERIC_PATH_SEGMENT_PATTERN.test(segment)) {
      path.push(segment);
      continue;
    }

    if (!isArrayIndexSegment(segment)) return undefined;
    path.push(Number(segment));
  }

  return path;
}

function setPathValue(
  source: Record<string, unknown>,
  path: SearchPathSegment[],
  value: unknown,
): Record<string, unknown> {
  const next = cloneDeep(source);
  set(next, path, value);
  return next;
}

function addParsedValue(
  source: Record<string, unknown>,
  key: string,
  value: string,
): Record<string, unknown> {
  const path = toSearchPath(key);
  if (!path) return source;

  const current = get(source, path);
  if (current === undefined) {
    return setPathValue(source, path, value);
  }

  if (Array.isArray(current)) {
    return setPathValue(source, path, [...current, value]);
  }

  if (isSerializableScalar(current)) {
    return setPathValue(source, path, [current, value]);
  }

  return source;
}

function scalarEntry(key: string, value: string | number | boolean): SearchParamEntry {
  return [key, String(value)];
}

function valueEntries(key: string, value: unknown): SearchParamEntry[] {
  if (value === null || value === undefined) return [];

  if (isSerializableScalar(value)) {
    return [scalarEntry(key, value)];
  }

  if (Array.isArray(value)) {
    return flatMap(value, (item, index) => {
      if (item === null || item === undefined) return [];
      if (isSerializableScalar(item)) return [scalarEntry(key, item)];
      return valueEntries(`${key}.${index}`, item);
    });
  }

  if (!isPlainObject(value)) return [];

  return flatMap(Object.entries(value), ([childKey, childValue]) =>
    valueEntries(key ? `${key}.${childKey}` : childKey, childValue),
  );
}

/**
 * Router search (de)serialization via URLSearchParams. Flat arrays use
 * repeated keys, while nested structures use dot paths with explicit numeric
 * indexes for arrays of objects. The resulting URLs avoid raw bracket
 * characters, which some HTTP front doors reject before the app handles SSR.
 */
const createSearchSerialization = (): SearchSerialization => {
  function stringifySearch(search: Record<string, unknown>): string {
    const params = new URLSearchParams(take(valueEntries("", search), MAX_SEARCH_PARAMS));
    const query = params.toString();
    return query ? `?${query}` : "";
  }

  function parseSearch(searchStr: string): Record<string, unknown> {
    const params = new URLSearchParams(searchStr);
    return take(Array.from(params), MAX_SEARCH_PARAMS).reduce<Record<string, unknown>>(
      (result, [key, value]) => addParsedValue(result, key, value),
      {},
    );
  }

  return { parseSearch, stringifySearch };
};

const searchSerialization = createSearchSerialization();

export function getSearchSerialization(): SearchSerialization {
  return searchSerialization;
}
